/**
 * The district as a map-app (§16): clean vector, top-down, muted fills,
 * labeled streets. Buildings are paths with tap handlers. People are dots
 * with three identity states. You are the one red mark.
 *
 * Reads state and content. Computes nothing: who is where comes from
 * `whereIs`, what you know from `knownLocations`, what you make of a
 * person from their read layer.
 *
 * The drawing is turned a quarter turn so the river runs along the bottom
 * of a phone held upright. Map units are unchanged; only the view is.
 */

import { type MapData, type MapPoint, type NPC, type NpcId, whereIs, type WorldState } from '@sim/index.ts';
import { type ReactNode, useRef } from 'react';

interface Props {
  readonly state: WorldState;
  readonly map: MapData;
  /** A tap on a building, by location id. */
  readonly onBuilding: (id: string) => void;
  /** A tap on open ground, in map units. */
  readonly onGround: (point: MapPoint) => void;
  readonly onNpc: (id: NpcId) => void;
  /** Where you're walking to, if anywhere. Drawn as a dotted line. */
  readonly walkingTo: MapPoint | null;
}

/** Beyond this many units, a known person is a dot without a name. */
const LABEL_RANGE = 2.6;

/** Spread people standing at the same door so they don't stack. */
const CROWD: readonly MapPoint[] = [
  { x: 0.22, y: 0.2 },
  { x: -0.24, y: 0.2 },
  { x: 0.2, y: -0.22 },
  { x: -0.22, y: -0.24 },
  { x: 0.3, y: 0 },
  { x: -0.3, y: 0 },
];

function dotStyle(npc: NPC): { r: number; fill: string } {
  switch (npc.read.acquaintance) {
    case 'known':
      return { r: 0.11, fill: 'var(--dot-known)' };
    case 'seen':
      return { r: 0.1, fill: 'var(--dot-seen)' };
    default:
      return { r: 0.08, fill: 'var(--dot-stranger)' };
  }
}

/** Text that stays readable inside the turned drawing. */
function Upright({ x, y, className, anchor, children }: { x: number; y: number; className: string; anchor?: 'middle'; children: ReactNode }) {
  return (
    <text className={className} x={x} y={y} textAnchor={anchor} transform={`rotate(-90 ${x} ${y})`}>
      {children}
    </text>
  );
}

export function Map({ state, map, onBuilding, onGround, onNpc, walkingTo }: Props) {
  const world = useRef<SVGGElement>(null);
  const b = map.bounds;
  const you = state.player.position.point;
  const here = state.player.position.at;

  // rotate(90): (x, y) → (−y, x). The viewBox is the bounds, turned.
  const viewBox = `${-(b.y + b.height)} ${b.x} ${b.height} ${b.width}`;

  const toMapPoint = (clientX: number, clientY: number): MapPoint | null => {
    const g = world.current;
    const svg = g?.ownerSVGElement;
    if (!g || !svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = g.getScreenCTM();
    if (!ctm) return null;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };

  // People, placed at the door of wherever their routine has them.
  const standing = new globalThis.Map<string, NPC[]>();
  for (const npc of Object.values(state.npcs)) {
    const at = whereIs(npc, state.time);
    if (at === null) continue;
    standing.set(at, [...(standing.get(at) ?? []), npc]);
  }

  return (
    <svg
      className="map"
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${state.districtName}, a map`}
      onClick={(e) => {
        if ((e.target as Element).closest('[data-tappable]')) return;
        const p = toMapPoint(e.clientX, e.clientY);
        if (p) onGround(p);
      }}
    >
      <g ref={world} transform="rotate(90)">
        <rect x={b.x} y={b.y} width={b.width} height={b.height} fill="var(--paper)" />
        {map.water.map((d, i) => (
          <path key={`w${i}`} className="water" d={d} />
        ))}
        {map.filler.map((d, i) => (
          <path key={`f${i}`} className="filler" d={d} />
        ))}
        {map.streets.map((s) => (
          <path key={s.name} className="street" d={s.path} />
        ))}
        {map.rail.map((d, i) => (
          <path key={`r${i}`} className="rail" d={d} />
        ))}
        {map.streets.map((s) => (
          <text
            key={`${s.name}-label`}
            className="street-label"
            x={s.labelAt.x}
            y={s.labelAt.y}
            transform={`rotate(${s.labelAt.angle} ${s.labelAt.x} ${s.labelAt.y})`}
          >
            {s.name}
          </text>
        ))}

        {map.buildings.map((bld) => {
          const location = state.locations[bld.location as keyof typeof state.locations];
          const known = state.player.knownLocations.includes(bld.location as never);
          const atDoor = here === bld.location;
          return (
            <g key={bld.location} data-tappable>
              <path
                className={`building${known ? ' known' : ''}${atDoor ? ' here' : ''}`}
                d={bld.path}
                role="button"
                tabIndex={0}
                aria-label={known && location ? location.name : 'a building'}
                onClick={(e) => {
                  e.stopPropagation();
                  onBuilding(bld.location);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onBuilding(bld.location);
                }}
              />
              {known && location && (
                <Upright className="building-label" x={bld.labelAt.x} y={bld.labelAt.y} anchor="middle">
                  {bld.label ?? location.name}
                </Upright>
              )}
            </g>
          );
        })}

        {walkingTo && <path className="you-path" d={`M ${you.x} ${you.y} L ${walkingTo.x} ${walkingTo.y}`} />}

        {[...standing.entries()].flatMap(([locationId, npcs]) => {
          const door = state.locations[locationId as keyof typeof state.locations]?.entrance;
          if (!door) return [];
          return npcs.map((npc, i) => {
            const off = CROWD[i % CROWD.length]!;
            const x = door.x + off.x;
            const y = door.y + off.y;
            const { r, fill } = dotStyle(npc);
            const near = Math.hypot(x - you.x, y - you.y) <= LABEL_RANGE;
            return (
              <g
                key={npc.id}
                className="npc"
                data-tappable
                role="button"
                tabIndex={0}
                aria-label={npc.read.acquaintance === 'known' ? npc.name : 'someone'}
                onClick={(e) => {
                  e.stopPropagation();
                  onNpc(npc.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onNpc(npc.id);
                }}
              >
                <circle cx={x} cy={y} r={r + 0.12} fill="transparent" />
                <circle cx={x} cy={y} r={r} fill={fill} />
                {npc.read.acquaintance === 'known' && near && (
                  <Upright className="npc-label" x={x + 0.06} y={y - 0.18}>
                    {npc.name.split(' ')[0]}
                  </Upright>
                )}
              </g>
            );
          });
        })}

        <circle className="you" cx={you.x} cy={you.y} r={0.13} />
      </g>
    </svg>
  );
}
